/**
 * SE-6796: session-scoped idempotency registry for the default-board auto-create.
 *
 * @remarks
 * The player-kanban screen deliberately creates a board when an account has
 * none — an account must always have at least one board. The defect this
 * module addresses is that it created a *duplicate*: the creation effect lived
 * entirely inside `useKanbanBoard`, so its `isCreatingBoard` guard only spanned
 * a single mount. A later mount that read an empty board list (transiently, or
 * because the read never settled) created another board, permanently. 2,365
 * boards accumulated in 31 hours on staging that way.
 *
 * Module scope is the point: unlike a `useRef`, this survives unmount/remount,
 * navigation, and Fast Refresh within one JS session, so the second mount can
 * see that the first already issued a create for the same identity.
 *
 * The key mirrors the identity a server-side uniqueness constraint would use —
 * `(organization_id, source_type, source_id)` — so the client guard and the
 * durable fix agree on what "the same board" means.
 *
 * ## What this CANNOT do
 *
 * This is a same-session guard. Two browser tabs, two devices, or a reload
 * mid-create each get a fresh module instance and can still race into a
 * duplicate. Only a uniqueness constraint in the database makes a duplicate
 * structurally impossible. Treat this as narrowing the window, not closing it.
 */

/**
 * Maximum create attempts per identity per session.
 *
 * A create that fails releases its slot so the account is not left permanently
 * board-less by one transient error, but the release is bounded — without the
 * cap a persistently failing mutation would re-fire on every render, which is
 * the retry storm the original code already suffered.
 */
const DEFAULT_BOARD_CREATE_ATTEMPT_LIMIT = 2;

/**
 * Per-identity creation record.
 * @property claimed - A create is in flight, or has already completed
 * @property count - Attempts ever made, retained across releases so the cap
 *   survives a claim being handed back after a failure
 */
interface DefaultBoardAttempt {
  readonly claimed: boolean;
  readonly count: number;
}

/**
 * The registry, held in a ref-shaped box so the module needs no reassignable
 * binding. Every write replaces the whole record map — no entry is ever
 * modified in place.
 */
const registry: { current: Readonly<Record<string, DefaultBoardAttempt>> } = {
  current: {},
};

/**
 * Build the identity key for a default (WATCHLIST-sourced) board.
 * @param organizationId - Owning organization; may be absent on a partial session
 * @param sourceType - Board source type, e.g. `WATCHLIST`
 * @param sourceId - Source entity id (the watchlist the board is bound to)
 * @returns A stable key, or null when the identity is not fully known yet
 */
export const buildDefaultBoardIdentityKey = (
  organizationId: string | null | undefined,
  sourceType: string,
  sourceId: string | null | undefined
): string | null => {
  if (!sourceId) {
    return null;
  }
  return `${organizationId ?? "-"}:${sourceType}:${sourceId}`;
};

/**
 * Whether a create has already been issued for this identity in this session.
 * @param key - Identity key from {@link buildDefaultBoardIdentityKey}
 * @returns True when another mount already owns the create
 */
export const hasAttemptedDefaultBoard = (key: string): boolean =>
  registry.current[key]?.claimed === true;

/**
 * Claim the identity before issuing the mutation.
 * @remarks
 * Must be called synchronously *before* `createBoardMutation`, so a concurrent
 * mount rendering in the same tick observes the claim rather than the empty
 * board list.
 * @param key - Identity key from {@link buildDefaultBoardIdentityKey}
 */
export const markDefaultBoardAttempted = (key: string): void => {
  registry.current = {
    ...registry.current,
    [key]: { claimed: true, count: (registry.current[key]?.count ?? 0) + 1 },
  };
};

/**
 * Release a claim after an observed creation failure, up to the attempt cap.
 * @param key - Identity key from {@link buildDefaultBoardIdentityKey}
 */
export const releaseDefaultBoardAttempt = (key: string): void => {
  const attempt = registry.current[key];
  if (!attempt || attempt.count >= DEFAULT_BOARD_CREATE_ATTEMPT_LIMIT) {
    return;
  }
  registry.current = {
    ...registry.current,
    [key]: { ...attempt, claimed: false },
  };
};

/**
 * Clear all registry state. Test-only — production code never resets a session.
 */
export const resetDefaultBoardCreationRegistry = (): void => {
  registry.current = {};
};
